import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFilter } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";
import { normalizeUserFilterExpression } from "#/utils/filterExpressionUtils";

const fetchStartMilkingPage = async ({ rejecId, page, sort, filter }) => {
  const baseFilter = new ApiFilter()
    .equal("zival.statusSifra", "AKTIVNA")
    .equal("zival.rejecId", rejecId)
    .equal("zival.spolSifra", "ZENSKI")
    .in("zival.speciesSifra", ["OVCA", "KOZA"])
    .or((orGroup) => {
      orGroup.isNull("molza.id");
      orGroup.isNull("molza.zacetekMolzeDatum");
    });

  let filterToUse = baseFilter;

  if (filter != null) {
    const baseExpr = (baseFilter?.toQueryString?.() || "").trim();
    const userExpr = normalizeUserFilterExpression(filter);

    if (baseExpr && userExpr) {
      filterToUse = `(${baseExpr})%20AND%20(${userExpr})`;
    }
  }

  const response = await ApiClient.get(
    "/api/zival/vwsamicemlecnost/page",
    undefined,
    page,
    filterToUse,
    sort,
    false
  );

  return response.data;
};

const fetchEndMilkingPage = async ({ rejecId, page, sort, filter }) => {
  const baseFilter = new ApiFilter()
    .equal("zival.statusSifra", "AKTIVNA")
    .equal("zival.rejecId", rejecId)
    .equal("zival.spolSifra", "ZENSKI")
    .in("zival.speciesSifra", ["OVCA", "KOZA"])
    .isNotNull("molza.id")
    .isNull("molza.konecMolzeDatum");

  let filterToUse = baseFilter;

  if (filter != null) {
    const baseExpr = (baseFilter?.toQueryString?.() || "").trim();
    const userExpr = normalizeUserFilterExpression(filter);

    if (baseExpr && userExpr) {
      filterToUse = `(${baseExpr})%20AND%20(${userExpr})`;
    }
  }

  const response = await ApiClient.get(
    "/api/zival/vwsamicemlecnost/page",
    undefined,
    page,
    filterToUse,
    sort,
    false
  );

  return response.data;
};

export const getKotitveForStartMilking = createAsyncThunk(
  "vwSamiceMlecnost/getKotitveForStartMilkingTab",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchStartMilkingPage(params);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const lookupKotitveForEndMilking = createAsyncThunk(
  "vwSamiceMlecnost/lookupKotitveForEndMilking",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchEndMilkingPage(params);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getAnimalsWithLactationRecords = createAsyncThunk(
  "vwSamiceMlecnost/getAnimalsWithLactationRecords",
  async ({ rejecId, page, filterString, sort }, { rejectWithValue }) => {
    try {
      const filter = new ApiFilter()
        .equal("zival.statusSifra", "AKTIVNA")
        .equal("zival.rejecId", rejecId)
        .equal("zival.spolSifra", "ZENSKI")
        .in("zival.speciesSifra", ["OVCA", "KOZA"]);

      if (filterString?.trim()) {
        filter.likeCaseInsensitive("zival.usesnaStevilka", filterString);
      }

      const response = await ApiClient.get(
        `/api/zival/vwsamicemlecnost/page`,
        undefined,
        page,
        filter,
        sort,
        false
      );

      const uniqueAnimals = [];
      const seenIds = new Set();

      response.data.content.forEach((record) => {
        if (record.zival && !seenIds.has(record.zival.id)) {
          seenIds.add(record.zival.id);
          uniqueAnimals.push(record.zival);
        }
      });

      return {
        ...response.data,
        content: uniqueAnimals,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getKotitveForEndMilking = createAsyncThunk(
  "vwSamiceMlecnost/getKotitveForEndMilkingTab",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchEndMilkingPage(params);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const lookupKotitveForStartMilking = createAsyncThunk(
  "vwSamiceMlecnost/lookupKotitveForStartMilking",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchStartMilkingPage(params);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const kotitveForMilkingSlice = createSlice({
  name: "kotitveForMilking",
  initialState: {
    isLoading: false,
    error: null,
    startMilkingRecordsPage: null,
    startMilkingLookupPage: null,
    endMilkingRecordsPage: null,
    endMilkingLookupPage: null,
    animalsWithLactationPage: null,
    byIdStart: {},
    byIdEnd: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getKotitveForStartMilking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.startMilkingRecordsPage = action.payload;

        action.payload.content.forEach((item) => {
          state.byIdStart[item.zivalId] = item;
        });
      })
      .addCase(getKotitveForStartMilking.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getKotitveForStartMilking.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getKotitveForEndMilking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.endMilkingRecordsPage = action.payload;

        action.payload.content.forEach((item) => {
          state.byIdEnd[item.zivalId] = item;
        });
      })
      .addCase(getKotitveForEndMilking.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getKotitveForEndMilking.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAnimalsWithLactationRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.animalsWithLactationPage = action.payload;
      })
      .addCase(getAnimalsWithLactationRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getAnimalsWithLactationRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(lookupKotitveForStartMilking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.startMilkingLookupPage = action.payload;
      })
      .addCase(lookupKotitveForStartMilking.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(lookupKotitveForStartMilking.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(lookupKotitveForEndMilking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.endMilkingLookupPage = action.payload;
      })
      .addCase(lookupKotitveForEndMilking.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(lookupKotitveForEndMilking.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      });
  },
});

export default kotitveForMilkingSlice.reducer;
